import React, { useRef } from "react";
import { Link, useParams } from "react-router-dom";
import { motion, useInView } from "framer-motion";
import { ArrowRight, Award, Sparkles, Star, Check } from "lucide-react";

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
};

const AnimatedSection = ({ children, className = "" }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  
  return (
    <motion.section
      ref={ref}
      initial="hidden"
      animate={isInView ? "visible" : "hidden"}
      variants={staggerContainer}
      className={className}
    >
      {children}
    </motion.section>
  );
};

// Event IP Logos
const EVENT_LOGOS = {
  sparc: "/assets/images/logos/sparc.png",
  inspire: "/assets/images/logos/inspire.png",
  edvantage: "/assets/images/logos/edvantage.png",
  acadelite: "/assets/images/logos/acadelite.png"
};

// Gallery images by event
const EVENT_IMAGES = {
  sparc: [],
  inspire: [
    "/assets/images/gallery/gallery6.jpg",
    "/assets/images/gallery/gallery12.jpg",
    "/assets/images/gallery/gallery3.jpg",
    "/assets/images/gallery/gallery13.jpg",
  ],
  edvantage: [
    "/assets/images/gallery/gallery2.jpg",
    "/assets/images/gallery/gallery9.jpg",
    "/assets/images/gallery/gallery10.jpg",
    "/assets/images/gallery/gallery11.jpg",
  ],
  acadelite: [
    "/assets/images/gallery/gallery5.jpg",
    "/assets/images/gallery/gallery7.jpg",
    "/assets/images/gallery/gallery1.jpg",
    "/assets/images/gallery/gallery8.jpg",
  ]
};

// Learning Forums Data with FULL content
const FORUMS_DATA = {
  sparc: {
    name: "SPARC",
    fullName: "Symposium for Promoting Academic Research and Collaboration",
    tagline: "Igniting Academic Excellence Through Collaboration",
    description: [
      "SPARC is a distinguished intellectual platform curated by GSLC, designed to bring together visionary school leaders, educationists, policymakers, and innovators from across the nation. More than a conference, SPARC is a confluence of ideas — a space where experience meets inquiry, where leadership meets responsibility, and where the future of schooling is examined with depth and courage.",
      "At its core, SPARC is built on dialogue. It fosters meaningful conversations around emerging educational paradigms, leadership challenges, generational shifts, technology integration, governance models, and the evolving purpose of schools in a rapidly transforming world.",
      "Each edition of SPARC is thoughtfully themed to reflect the forces reshaping education. From philosophical reflections to strategic frameworks, SPARC creates an environment where ideas are not just discussed — they are refined, challenged, and elevated.",
      "By bringing together diverse perspectives under one roof, SPARC strengthens a national community of educators committed to excellence, integrity, and long-term impact."
    ],
    conclusion: "SPARC is not just about anticipating change. It is about shaping it — responsibly, collaboratively, and courageously.",
    logo: EVENT_LOGOS.sparc
  },
  acadelite: {
    name: "Acadelite",
    fullName: "Celebrating Educational Excellence",
    tagline: "Excellence with Ethics. Leadership with Vision. Innovation with Impact.",
    description: [
      "Acadelite is GSLC's flagship celebration of educational excellence — a distinguished platform that honours visionary school leaders, transformative institutions, and path-breaking practices shaping the future of education.",
      "More than an awards ceremony, Acadelite is a movement of recognition with responsibility. It acknowledges not only achievement, but impact — leadership that inspires, innovation that transforms, and commitment that elevates learning ecosystems.",
      "Curated with depth and integrity, Acadelite brings together educationists, trustees, principals, and thought leaders from across the nation to celebrate milestones that redefine standards in schooling.",
      "Each edition of Acadelite is anchored in a powerful symbolic theme — representing strength, vision, evolution, and the relentless pursuit of excellence. The platform serves as a reminder that education is not merely administered; it is led with purpose."
    ],
    values: [
      "Excellence with Ethics",
      "Leadership with Vision",
      "Innovation with Impact",
      "Recognition with Meaning"
    ],
    conclusion: "It is not just about awarding success. It is about spotlighting those who are shaping the future of learning — courageously and consciously.",
    logo: EVENT_LOGOS.acadelite
  },
  inspire: {
    name: "INSPIRE",
    fullName: "International Summit for Planning and Incubating Reforms in Education",
    tagline: "Where Disruption Meets Dreams",
    description: [
      "INSPIRE is GSLC's dynamic platform dedicated to igniting purpose, passion, and possibility within the education community. Designed as a catalyst for growth, INSPIRE brings together educators, leaders, and changemakers who believe that meaningful transformation begins with mindset.",
      "It is a space where ideas are not merely presented, but felt — where stories of resilience, innovation, and leadership spark renewed energy within the schooling ecosystem.",
      "INSPIRE focuses on empowering educators to move beyond routine and rediscover the deeper purpose of their profession. Through powerful addresses, reflective dialogues, and experiential engagements, the platform nurtures clarity, courage, and conviction in leadership.",
      "At its heart, INSPIRE is about awakening potential — in institutions, in classrooms, and in individuals."
    ],
    conclusion: "It reinforces a simple yet profound belief: When educators feel inspired, education itself evolves. INSPIRE is not just an event. It is a reminder of why we began — and why we must continue.",
    logo: EVENT_LOGOS.inspire
  },
  edvantage: {
    name: "Edvantage",
    fullName: "Strategic Advantage for Future-Ready Schools",
    tagline: "Strategic Growth. Future-Ready Leadership. Intelligent Innovation.",
    description: [
      "Edvantage is GSLC's forward-looking platform dedicated to empowering schools with strategic advantage in an evolving educational landscape.",
      "Designed for progressive institutions, Edvantage focuses on equipping school leaders with practical insights, innovative frameworks, and future-ready strategies that strengthen academic excellence, operational efficiency, and institutional growth.",
      "In a time when education is shaped by technological advancement, generational shifts, and changing expectations, Edvantage serves as a bridge between vision and execution. It enables schools to adapt with clarity, compete with confidence, and lead with purpose.",
      "Through curated engagements, expert-led discussions, and solution-oriented collaborations, Edvantage supports schools in building sustainable models that combine quality, innovation, and long-term impact."
    ],
    values: [
      "Strategic Growth",
      "Future-Ready Leadership",
      "Intelligent Innovation",
      "Measurable Impact"
    ],
    conclusion: "Edvantage is not just about staying relevant. It is about staying ahead — responsibly and decisively.",
    logo: EVENT_LOGOS.edvantage
  }
};

// Individual Forum Detail Component
const ForumDetail = ({ forumId }) => {
  const forum = FORUMS_DATA[forumId];
  const images = EVENT_IMAGES[forumId] || [];
  
  if (!forum) {
    return (
      <div className="pt-24 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-heading text-slate-800 mb-4">Forum Not Found</h1>
          <Link to="/learning-forums" className="btn-gradient">View All Forums</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-24">
      {/* Hero */}
      <AnimatedSection className="section-padding bg-gradient-to-br from-yellow-50 via-white to-blue-50">
        <div className="container-custom">
          <motion.div variants={fadeInUp} className="max-w-4xl mx-auto text-center">
            <img 
              src={forum.logo} 
              alt={forum.name} 
              className="h-44 md:h-52 mx-auto mb-6 object-contain" 
            />
            <h1 className="font-heading text-4xl md:text-6xl font-semibold mb-4 text-slate-800">
              {forum.name}
            </h1>
            <p className="font-accent text-red-600 text-base md:text-lg tracking-wide mb-6">{forum.fullName}</p>
            <p className="text-xl text-slate-600 italic max-w-2xl mx-auto">
              “{forum.tagline}”
            </p>
          </motion.div>
        </div>
      </AnimatedSection>

      {/* Full Description */}
      <AnimatedSection className="section-padding">
        <div className="container-custom">
          <motion.div variants={fadeInUp} className="max-w-4xl mx-auto">
            <div className="prose prose-lg max-w-none">
              {forum.description.map((para, idx) => (
                <p key={idx} className="text-slate-600 leading-relaxed mb-6 text-lg">{para}</p>
              ))}
            </div>
          </motion.div>
        </div>
      </AnimatedSection>

      {/* Values Section if available */}
      {forum.values && (
        <AnimatedSection className="section-padding bg-gradient-to-br from-blue-50 via-white to-yellow-50">
          <div className="container-custom">
            <motion.div variants={fadeInUp} className="text-center mb-12">
              <h2 className="font-heading text-3xl md:text-4xl font-medium text-slate-800">
                {forum.name} Stands For
              </h2>
            </motion.div>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto">
              {forum.values.map((item, idx) => (
                <motion.div key={idx} variants={fadeInUp} className="card-light rounded-xl p-6 flex items-start gap-4 text-center">
                  <div className="w-full">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-yellow-400 to-red-500 flex items-center justify-center mx-auto mb-4">
                      <Star className="w-6 h-6 text-white" />
                    </div>
                    <p className="text-slate-700 font-heading text-lg font-medium">{item}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </AnimatedSection>
      )}

      {/* Gallery */}
      {images.length > 0 && (
        <AnimatedSection className="section-padding">
          <div className="container-custom">
            <motion.div variants={fadeInUp} className="text-center mb-12">
              <h2 className="font-heading text-3xl md:text-4xl font-medium text-slate-800">
                Event <span className="gradient-text">Gallery</span>
              </h2>
            </motion.div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-5xl mx-auto">
              {images.slice(0, 4).map((img, idx) => (
                <motion.div key={idx} variants={fadeInUp} className="aspect-square rounded-2xl overflow-hidden">
                  <img src={img} alt={`${forum.name} event ${idx + 1}`} className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
                </motion.div>
              ))}
            </div>
            <motion.div variants={fadeInUp} className="text-center mt-8">
              <Link to="/gallery" className="text-blue-700 hover:text-red-600 font-medium inline-flex items-center gap-2">
                View More Photos <ArrowRight size={16} />
              </Link>
            </motion.div>
          </div>
        </AnimatedSection>
      )}

      {/* Conclusion */}
      {forum.conclusion && (
        <AnimatedSection className="section-padding bg-gradient-to-r from-slate-900 via-blue-900 to-slate-900">
          <div className="container-custom">
            <motion.div variants={fadeInUp} className="max-w-3xl mx-auto text-center">
              <p className="text-xl text-white leading-relaxed italic">
                {forum.conclusion}
              </p>
            </motion.div>
          </div>
        </AnimatedSection>
      )}

      {/* CTA */}
      <AnimatedSection className="section-padding">
        <div className="container-custom">
          <motion.div variants={fadeInUp} className="text-center">
            <Link to="/contact" className="btn-gradient inline-flex items-center gap-2">
              Register Your Interest <ArrowRight size={18} />
            </Link>
          </motion.div>
        </div>
      </AnimatedSection>
    </div>
  );
};

// Learning Forums Overview Page - Order: SPARC, Acadelite, INSPIRE, Edvantage
const ForumsOverview = () => {
  // Specific order as requested
  const forumOrder = ['sparc', 'acadelite', 'inspire', 'edvantage'];
  const forums = forumOrder.map(id => ({ id, ...FORUMS_DATA[id] }));

  return (
    <div className="pt-24">
      {/* Hero */}
      <AnimatedSection className="section-padding bg-gradient-to-br from-yellow-50 via-white to-blue-50">
        <div className="container-custom">
          <motion.div variants={fadeInUp} className="max-w-4xl mx-auto text-center">
            <span className="font-accent text-red-600 text-sm tracking-[0.2em] uppercase">Our Flagship Initiatives</span>
            <h1 className="font-heading text-4xl md:text-6xl font-semibold mt-4 mb-6 text-slate-800">
              Conclaves & Program <span className="gradient-text">IPs</span>
            </h1>
            <p className="text-lg text-slate-600 leading-relaxed max-w-2xl mx-auto">
              GSLC's signature learning forums bring together school leaders, educators, and stakeholders 
              for transformative experiences that shape the future of education.
            </p>
          </motion.div>
        </div>
      </AnimatedSection>

      {/* Forums Grid */}
      <AnimatedSection className="section-padding">
        <div className="container-custom">
          <div className="grid md:grid-cols-2 gap-8">
            {forums.map((forum) => (
              <motion.div key={forum.id} variants={fadeInUp}>
                <Link 
                  to={`/learning-forums/${forum.id}`}
                  className="block card-light rounded-2xl p-8 hover-lift h-full"
                >
                  <img 
                    src={forum.logo} 
                    alt={forum.name} 
                    className="h-32 mb-6 object-contain" 
                  />
                  <h3 className="font-heading text-2xl text-slate-800 mb-2">{forum.name}</h3>
                  <p className="text-sm text-red-600 font-accent mb-4">{forum.fullName}</p>
                  <p className="text-slate-600 text-sm line-clamp-3 mb-4">
                    {forum.description[0]}
                  </p>
                  <span className="text-blue-700 font-medium inline-flex items-center gap-2 group">
                    Learn More 
                    <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                  </span>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </AnimatedSection>
    </div>
  );
};

// Main Learning Forums Page Component
const LearningForumsPage = () => {
  const { forumId } = useParams();
  
  if (forumId) {
    return <ForumDetail forumId={forumId} />;
  }
  
  return <ForumsOverview />;
};

export default LearningForumsPage;
